import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, Bookmark, Boxes, Building2, CircleHelp, CreditCard, Database, FileDown, Grid3x3, Hexagon, Home, Inbox, LayoutDashboard, LogIn, LogOut, Menu,
  Globe2, MessageSquareText, PackageSearch, Radar, Settings2, Ship, ShieldCheck, ShoppingCart, Target, UserCircle2, UserCog, Users, X, type LucideIcon,
} from 'lucide-react';
import { useMeta } from '../hooks/useEngine';
import { ExportDialog } from '../features/export/ExportDialog';
import { SiteFooter } from './SiteFooter';
import { ROLE_LABEL, isTradeAdmin, useAuth, type ModuleMode } from '../features/auth/AuthProvider';
import { trade } from '../lib/trade';
import { SITE, getWhatsAppHelplineUrl } from '../lib/site';
import { WhatsAppIcon } from './WhatsAppIcon';
import type { Me } from '../types/trade';

interface NavItem { to: string; label: string; icon: LucideIcon; badge?: number }
interface NavGroup { label: string; items: NavItem[] }

export const ENGINE_NAV: NavItem[] = [
  { to: '/', label: 'Decision', icon: Target },
  { to: '/sensitivity', label: 'Sensitivity', icon: Grid3x3 },
  { to: '/uncertainty', label: 'Uncertainty', icon: Activity },
  { to: '/assumptions', label: 'Assumptions', icon: Database },
  { to: '/scenarios', label: 'Scenarios', icon: Bookmark },
  { to: '/advisor', label: 'Advisor', icon: MessageSquareText },
  { to: '/settings', label: 'Settings', icon: Settings2 },
];
const ENGINE_PATHS = ENGINE_NAV.map((n) => n.to);

export function navFor(user: Me | null, engineAllowed: boolean, unread: number, activeModule: ModuleMode = 'admin'): NavGroup[] {
  if (!user) {
    return engineAllowed ? [{ label: 'PLANT DECISION', items: ENGINE_NAV }] : [];
  }

  // The same three reference pages for everyone: what moves between countries, what we trade, where it ships from.
  const insights: NavItem[] = [
    { to: '/trade/corridor', label: 'Market insights', icon: Globe2 },
    { to: '/trade/intelligence', label: 'Knowledge hub', icon: Radar },
    { to: '/trade/catalog', label: 'Product catalogue', icon: Boxes },
    { to: '/trade/ports', label: 'Ports', icon: Ship },
  ];

  // Buyers see who is selling; sellers see who is buying. One page each, no duplicates.
  if (user.role === 'buyer' || (user.role === 'super_admin' && activeModule === 'buyer')) {
    return [
      { label: 'BUYING', items: [
        { to: '/trade', label: 'Overview', icon: LayoutDashboard },
        { to: '/trade/directory?kind=seller', label: 'Who is selling', icon: Building2 },
        { to: '/trade/market', label: 'Live offers', icon: ShoppingCart },
        { to: '/trade/requirements', label: 'My requirements', icon: PackageSearch },
        { to: '/trade/enquiries', label: 'Enquiries', icon: Inbox, badge: unread },
      ] },
      { label: 'MARKET', items: insights },
    ];
  }

  if (user.role === 'seller' || (user.role === 'super_admin' && activeModule === 'seller')) {
    return [
      { label: 'SELLING', items: [
        { to: '/trade', label: 'Overview', icon: LayoutDashboard },
        { to: '/trade/directory?kind=buyer', label: 'Who is buying', icon: Building2 },
        { to: '/trade/market', label: 'Buyer requests', icon: ShoppingCart },
        { to: '/trade/listings', label: 'My listings', icon: PackageSearch },
        { to: '/trade/enquiries', label: 'Enquiries', icon: Inbox, badge: unread },
      ] },
      { label: 'MARKET', items: insights },
    ];
  }

  const groups: NavGroup[] = [
    { label: 'MANAGE', items: [
      { to: '/trade', label: 'Overview', icon: LayoutDashboard },
      ...(user.role === 'super_admin' ? [{ to: '/admin/users', label: 'Users & approvals', icon: Users }, { to: '/admin/billing', label: 'Billing', icon: CreditCard }] : []),
      { to: '/trade/review', label: 'Company leads', icon: ShieldCheck },
      { to: '/trade/discovery', label: 'Find companies', icon: Radar },
    ] },
    { label: 'MARKETPLACE', items: [
      { to: '/trade/directory', label: 'Companies', icon: Building2 },
      { to: '/trade/market', label: 'Live marketplace', icon: ShoppingCart },
      { to: '/trade/enquiries', label: 'Enquiries', icon: Inbox, badge: unread },
    ] },
    { label: 'MARKET', items: insights },
  ];
  if (engineAllowed) groups.push({ label: 'PLANT DECISION', items: ENGINE_NAV });
  return groups;
}

/** Account pages live in the sidebar footer, next to the signed-in person, so the main menu stays short. */
export function accountNav(user: Me | null): NavItem[] {
  if (!user) return [];
  const org: NavItem[] = user.role === 'buyer' || user.role === 'seller'
    ? [{ to: '/trade/profile', label: 'My company', icon: UserCircle2 }, { to: '/billing', label: 'Plan & billing', icon: CreditCard }]
    : [];
  return [...org, { to: '/account', label: 'Account', icon: UserCog }, { to: '/manual', label: 'Help', icon: CircleHelp }];
}

function isActive(path: string, to: string): boolean {
  if (to === '/' || to === '/trade') return path === to;
  return path === to || path.startsWith(`${to}/`);
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, engineAllowed, logout, activeModule } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const onEnginePage = ENGINE_PATHS.some((p) => isActive(location.pathname, p));
  const meta = useMeta(engineAllowed && onEnginePage);
  const dash = useQuery({ queryKey: ['trade-dashboard'], queryFn: ({ signal }) => trade.dashboard({}, signal), enabled: !!user, refetchInterval: 60_000 });
  const groups = navFor(user, engineAllowed, dash.data?.unread_enquiries ?? 0, activeModule);
  const account = accountNav(user);
  const current = [...groups.flatMap((g) => g.items), ...account].filter((n) => isActive(location.pathname, n.to)).sort((a, b) => b.to.length - a.to.length)[0];

  const platformLabel = activeModule === 'buyer'
    ? 'Buyer Platform'
    : activeModule === 'seller'
    ? 'Seller Platform'
    : 'Super Admin Platform';

  const userRole = activeModule === 'buyer' ? 'buyer' : activeModule === 'seller' ? 'seller' : user?.role;

  const brandCaption = activeModule === 'buyer'
    ? 'BUYER PROCUREMENT'
    : activeModule === 'seller'
    ? 'SELLER SUPPLY'
    : 'SUPER ADMIN PLATFORM';

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const conn = meta.isError ? { cls: 'bad', text: 'API unreachable' } : meta.data?.offline ? { cls: 'warn', text: 'Offline snapshot' } : meta.data ? { cls: 'ok', text: 'Live model' } : { cls: '', text: 'Connecting…' };
  const signOut = async () => { await logout(); navigate('/welcome', { replace: true }); };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">Skip to content</a>
      <aside className={`sidebar${menuOpen ? ' open' : ''}`} aria-label="Primary">
        <div className="row-between">
          <Link to={user ? '/trade' : (engineAllowed ? '/' : '/welcome')} className="brand">
            <span className="brand-mark"><Hexagon size={22} aria-hidden /></span>
            <span><span className="brand-name">Corridor Intelligence</span><span className="brand-caption">{brandCaption}</span></span>
          </Link>
          <button type="button" className="icon-button menu-button" style={{ color: '#3d5064' }} aria-label="Close navigation" onClick={() => setMenuOpen(false)}><X size={18} /></button>
        </div>


        <nav id="primary-nav" className="nav-groups">
          {groups.filter((g) => g.items.length).map((g) => (
            <div key={g.label}>
              <p className="nav-label">{g.label}</p>
              {g.items.map(({ to, label, icon: Icon, badge }) => (
                <NavLink key={to} to={to} end={to === '/' || to === '/trade'} className={isActive(location.pathname, to) ? 'active' : undefined}>
                  <Icon size={17} aria-hidden /><span>{label}</span>{badge ? <span className="nav-badge" aria-label={`${badge} unread`}>{badge}</span> : null}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          {user ? (
            <>
              <div className="sidebar-user">
                <span className="sidebar-avatar" aria-hidden>{(user.full_name || 'U').charAt(0).toUpperCase()}</span>
                <span className="sidebar-user-text"><strong>{user.full_name}</strong>
                  <span>{ROLE_LABEL[user.role]}{user.organization_name ? ` · ${user.organization_name}` : ''}</span></span>
                <button type="button" className="icon-button sidebar-signout" title="Sign out" aria-label="Sign out" onClick={() => void signOut()}><LogOut size={15} aria-hidden /></button>
              </div>
              <nav className="sidebar-account" aria-label="Account">
                {account.map(({ to, label, icon: Icon }) => (
                  <NavLink key={to} to={to} className={isActive(location.pathname, to) ? 'active' : undefined}><Icon size={14} aria-hidden /><span>{label}</span></NavLink>
                ))}
              </nav>
            </>
          ) : (
            <>
              <strong>{engineAllowed ? 'Screening model' : 'Lithium trade'}</strong>
              {engineAllowed ? 'Grade B inputs are indicative, grade C are placeholders.' : 'Direct trade intelligence & counterparty matching.'}
              <div className="row sidebar-actions"><Link to="/welcome" className="sidebar-link"><LogIn size={13} aria-hidden />Sign in</Link></div>
            </>
          )}
          {meta.data && onEnginePage && <div style={{ marginTop: 8 }} className="tiny">Engine {meta.data.meta.engine_version} · data {meta.data.meta.assumptions_version}</div>}
        </div>
      </aside>
      {menuOpen && <button type="button" className="nav-backdrop" aria-label="Close navigation" onClick={() => setMenuOpen(false)} />}
      <div className="workspace">
        <header className="topbar">
          <div className="topbar-left">
            <button ref={menuButton} type="button" className="icon-button menu-button" aria-label="Open navigation" aria-expanded={menuOpen} aria-controls="primary-nav" onClick={() => setMenuOpen(true)}><Menu size={20} /></button>
            <Link to="/welcome" className="button secondary sm" style={{ gap: 6, marginRight: 4 }} title="Go to Portals Home">
              <Home size={15} aria-hidden /> Home
            </Link>
            <span className="platform-tag">{platformLabel}</span>
            <span className="topbar-title">{current?.label ?? (location.pathname.startsWith('/account') ? 'Account' : '')}</span>
          </div>
          <div className="topbar-actions">
            {onEnginePage && engineAllowed && (
              <>
                <span className={`conn ${conn.cls}`} role="status"><span />{conn.text}</span>
                <button type="button" className="button secondary sm" onClick={() => setExporting(true)} disabled={meta.data?.offline || meta.isError}>
                  <FileDown size={15} aria-hidden />Export PDF
                </button>
              </>
            )}
            {user && (userRole === 'buyer' || userRole === 'seller') && (
              <a
                href={getWhatsAppHelplineUrl(userRole)}
                target="_blank"
                rel="noopener noreferrer"
                className="button ghost sm"
                style={{ gap: 6, color: '#16a34a' }}
                title={`WhatsApp Helpline: ${SITE.whatsappDisplay}`}
              >
                <WhatsAppIcon size={14} style={{ color: '#16a34a' }} />
                <span>Helpline</span>
              </a>
            )}
            {user ? (
              <Link to="/account" className="user-chip" title={user.email}><UserCircle2 size={17} aria-hidden /><span>{user.full_name}</span><span className="tiny muted">{ROLE_LABEL[user.role]}</span></Link>
            ) : (
              <Link to="/welcome" className="button primary sm"><LogIn size={14} aria-hidden />Sign in</Link>
            )}
          </div>
        </header>
        <main id="main" className="main" tabIndex={-1}><Outlet /></main>
        <SiteFooter role={userRole} note={engineAllowed ? (isTradeAdmin(user) ? 'Discovered companies stay unverified until staff check them.' : 'Numbers come from the deterministic engine; companies show where their data came from.') : 'Verified trading directory, enquiries and product catalogue.'} />
      </div>
      {exporting && <ExportDialog onClose={() => setExporting(false)} />}
    </div>
  );
}
